<template>
  <div id="manager-card" class="manager-card">
    <div class="manager-card__content">
      <div v-if="!isMobile" class="manager-card__title">Your manager:</div>
      <div class="manager-card__block mb-1 flex items-center">
        <UIAvatar
          width="24"
          height="24"
          :user="manager"
          class="manager-card__avatar"
          :class="{ '[&>span]:text-caption-2': !isMobile }"
          :src="manager.image"
          no-border
          alt="team-member-1"
        />
        <p v-if="!isMobile && manager?.name" class="manager-card__name">{{ manager.name }}</p>
      </div>
      <a v-if="manager.phone_country" :href="callUrl" class="manager-card__phone">
        <UIIcon v-if="isMobile" size="xs" name="whatsapp-fill" />
        <UIIcon v-else size="xs" name="phone" />
        <span>+{{ manager.phone_country.phone_code }} </span>{{ manager.phone }}
      </a>
      <a v-if="manager?.email" :href="`mailto:${manager.email}`" class="manager-card__email">
        <UIIcon size="xs" name="email" />
        <span>{{ manager.email }}</span> </a
      ><a
        v-if="manager.phone_country"
        :href="`tel:+${manager.phone_country.phone_code}${manager.phone}`"
        class="manager-card__call"
      >
        <UIButton v-if="isMobile" id="call-manager" size="small" variant="secondary"> Call manager </UIButton>
      </a>
      <FeaturesGradient v-if="!isMobile" left="-52" bottom="-51" color="gold-primary-100" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { User } from '~/types/auth'

const { isMobile } = useAppBreakpoints()

const props = defineProps<{
  manager: User
}>()

const callUrl = computed(() => {
  const phone = `+${props.manager.phone_country.phone_code}${props.manager.phone}`
  if (isMobile) return `https://api.whatsapp.com/send?phone=${phone}&text=Hey`

  return `tel:${phone}`
})
</script>

<style scoped lang="scss">
@import '@/styles/mixins.scss';
.manager-card {
  &__title {
    font-weight: 500;
    margin-bottom: 10px;
  }

  &__content {
    border-radius: 16px;
    border: 1px solid $black-20;
    background: rgba(255, 255, 255, 0.3);
    padding: 16px;
    width: 100%;
    min-height: 142px;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    @media screen and (max-width: 1023px) {
      min-height: auto;
      border-radius: 24px;
      border: 1px solid $black-60;
      background-color: white;
      border-width: 0.5px;
      display: grid;
      grid-template-rows: repeat(2, minmax(0, 1fr));
      grid-template-columns: 70px 1fr;
      padding: 12px;
      gap: 8px;
    }
  }

  &__block {
    @media screen and (max-width: 1023px) {
      grid-row: span 2 / span 2;
    }
  }

  &__avatar {
    @media screen and (max-width: 1023px) {
      width: 70px !important;
      height: 70px !important;
    }
  }

  &__name {
    margin-left: 8px;
    @include body2();
  }

  &__phone,
  &__email {
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    color: $black-100;
    @include body2();
    width: 100%;
    @media screen and (max-width: 1023px) {
      grid-column: span 2 / span 2;
      margin-bottom: 0;
      background-color: $bg;
      padding: 5px 8px;
      border-radius: 8px;
      .icon {
        margin-left: 0;
      }
    }

    span {
      margin-left: 8px;
    }
  }

  .tooltip {
    width: 100%;
  }

  &__email {
    margin-bottom: 0;
    width: 100%;
    overflow: hidden;

    .icon {
      flex-shrink: 0;
    }

    span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  &__call {
    grid-row-start: 3;
    grid-column-start: 1;
    grid-column-end: 4;
    .button {
      width: 100%;
      border-radius: 8px;
      font-size: 14px;
    }
  }
}
</style>
